/**
 * PlanChangeCalculator — Paket C (premium-checkout-redesign)
 *
 * "Tarif wechseln"-Karte auf /account/subscription (Design-Referenz:
 * subscription.html #change). Live-Rechner für Upgrade (Laufzeitpreis minus
 * Restwert, ggf. Zusatztage aus Guthaben-Überschuss) und Downgrade (Restwert
 * wird vollständig in Laufzeit des Zielplans umgerechnet, 0,00 $ fällig).
 *
 * Restwert/Resttage kommen — sofern vorhanden — aus der BE-Vorschau
 * (POST /api/v2/subscriptions/preview_upgrade), NICHT selbst gerechnet;
 * planMath dient nur als Fallback, falls die Vorschau (noch) fehlschlägt.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import type { Plan } from './useSubscriptionPlans'
import { displayPrice } from './useSubscriptionPlans'
import type { Subscription } from './useSubscription'
import type { PreviewUpgradeResult } from '../premium/usePreviewUpgrade'
import type { HotspotQuota } from '../settings/useAccount'
import { planQuotas } from '../premium/planQuotas'
import {
  planRank,
  remainingValue,
  convertCreditToDays,
  addDays,
  dueToday,
  overflowDays,
  splitMonthsDays,
  formatMoney,
  formatDateLong,
} from '../premium/planMath'
import { DURATION_OPTIONS, type DurationMonths } from './subscriptionUtils'
import { ConfirmChangeModal } from './ConfirmChangeModal'

type PlanChangeCalculatorProps = {
  currentPlan: Plan
  availablePlans: Plan[]
  subscription: Subscription
  hotspotQuota?: HotspotQuota
  targetPlanId: string
  onTargetPlanChange: (planId: string) => void
  preview: PreviewUpgradeResult | undefined
  previewFetching: boolean
  changePlan: (planId: string, durationMonths: number) => Promise<void>
  isMutating: boolean
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

function daysRemainingFallback(currentPeriodEnd: string): number {
  const ms = new Date(currentPeriodEnd).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)))
}

function quotaLabel(value: number | 'unlimited' | 'unknown'): string {
  return typeof value === 'number' ? String(value) : ''
}

export function PlanChangeCalculator({
  currentPlan,
  availablePlans,
  subscription,
  hotspotQuota,
  targetPlanId,
  onTargetPlanChange,
  preview,
  previewFetching,
  changePlan,
  isMutating,
  onSuccess,
  onError,
}: PlanChangeCalculatorProps) {
  const { t } = useTranslation('subscription')
  const navigate = useNavigate()
  const [term, setTerm] = useState<DurationMonths>(1)
  const [showConfirm, setShowConfirm] = useState(false)

  const targetPlan = availablePlans.find((p) => p.id === targetPlanId) ?? null
  const chips = useMemo(
    () => availablePlans.filter((p) => p.id !== currentPlan.id && p.id !== 'free'),
    [availablePlans, currentPlan.id],
  )
  const up = targetPlan
    ? planRank(availablePlans, targetPlan.id) > planRank(availablePlans, currentPlan.id)
    : true

  const fallbackDays = daysRemainingFallback(subscription.current_period_end)
  const daysRemaining =
    preview?.ok && preview.days_remaining != null ? preview.days_remaining : fallbackDays
  const credit =
    preview?.ok && preview.proration_credit != null
      ? preview.proration_credit
      : remainingValue(currentPlan.price_eur_monthly, daysRemaining)
  const currency = currentPlan.currency

  if (!targetPlan) return null

  const durationEntry = targetPlan.durations?.find((d) => d.months === term)
  const discountPct = durationEntry?.discount_pct

  // Issue BE#24 — welcher Pfad greift bei Bestätigung. 'conversion' (Krypto-/
  // Legacy-Premium ohne Stripe-Abo): Restlaufzeit wird pro rata umgerechnet,
  // keine Laufzeit-Auswahl, keine Zahlung. Solange keine Vorschau vorliegt
  // (Fehler/noch nicht geladen), Fallback auf den bisherigen Stripe-Swap-Pfad.
  const mode: 'stripe_swap' | 'conversion' | 'checkout' = preview?.ok ? preview.mode : 'stripe_swap'
  const isConversionUpgrade = up && mode === 'conversion'

  // --- Upgrade ---
  const termTotal = displayPrice(targetPlan, term)
  const due = isConversionUpgrade ? 0 : dueToday(termTotal, credit)
  const overflow = overflowDays(termTotal, credit, targetPlan.price_eur_monthly)
  const upUntil =
    isConversionUpgrade && preview?.ok && preview.conversion_expires_at
      ? formatDateLong(new Date(preview.conversion_expires_at))
      : formatDateLong(addDays(new Date(), term * 30.44 + overflow))
  const targetQuota = planQuotas(targetPlan)

  // --- Downgrade ---
  const convertedDays =
    preview?.ok && preview.converted_days != null
      ? preview.converted_days
      : convertCreditToDays(credit, targetPlan.price_eur_monthly)
  const downUntil =
    preview?.ok && preview.new_expires_at
      ? formatDateLong(new Date(preview.new_expires_at))
      : formatDateLong(addDays(new Date(), convertedDays))
  const { months: convMonths, days: convDays } = splitMonthsDays(convertedDays)
  const deltaDays = Math.round(convertedDays - daysRemaining)
  const mobileUsed = hotspotQuota?.mobile.used ?? 0
  const iotUsed = hotspotQuota?.iot.used ?? 0
  const overQuota =
    (typeof targetQuota.mobile === 'number' && mobileUsed > targetQuota.mobile) ||
    (typeof targetQuota.iot === 'number' && iotUsed > targetQuota.iot)

  function handleCtaClick() {
    if (up) {
      if (due > 0) {
        void navigate({ to: '/checkout', search: { plan: targetPlan!.id, term } })
        return
      }
      setShowConfirm(true)
    } else {
      setShowConfirm(true)
    }
  }

  function handleConfirmSuccess() {
    setShowConfirm(false)
    onSuccess(t(up ? 'planChangeCalculator.success.up' : 'planChangeCalculator.success.down'))
    if (!up) {
      void navigate({
        to: '/checkout/success',
        search: { plan: targetPlan!.id, term: 1, method: 'credit' },
      })
    }
  }

  const confirmBody = up
    ? t('planChangeCalculator.confirmBody.upZero', { plan: targetPlan.name })
    : t('planChangeCalculator.confirmBody.down', {
        plan: targetPlan.name,
        mobile: quotaLabel(targetQuota.mobile),
        iot: quotaLabel(targetQuota.iot),
        credit: formatMoney(credit, currency),
        date: downUntil,
      })

  return (
    <div className="card card-pad-lg" id="change" data-testid="plan-change-calculator">
      <h3 style={{ margin: 0 }}>{t('planChangeCalculator.title')}</h3>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
        {t('planChangeCalculator.subtitle')}
      </p>

      <div className="plan-pick" role="group" aria-label={t('planChangeCalculator.pickAriaLabel')}>
        {chips.map((p) => {
          const isUp = planRank(availablePlans, p.id) > planRank(availablePlans, currentPlan.id)
          return (
            <button
              key={p.id}
              type="button"
              className={`chip ${isUp ? 'dir-up' : 'dir-down'}${p.id === targetPlanId ? ' is-active' : ''}`}
              onClick={() => onTargetPlanChange(p.id)}
              data-testid={`plan-chip-${p.id}`}
            >
              {isUp ? '↑' : '↓'} {p.name}
            </button>
          )
        })}
      </div>

      <div className="calc-box">
        <div className="calc-head">
          <span data-testid="calc-title">{`${currentPlan.name} → ${targetPlan.name}`}</span>
          <span className={`pill ${up ? 'pill-primary' : 'pill-warning'}`} data-testid="calc-direction">
            {t(up ? 'planChangeCalculator.direction.up' : 'planChangeCalculator.direction.down')}
          </span>
        </div>
        <div className="calc-body" aria-busy={previewFetching}>
          {previewFetching ? (
            <div className="pulse" data-testid="calc-preview-skeleton">
              <div className="skeleton-text" style={{ height: 16, width: '60%' }} />
              <div className="skeleton-text" style={{ height: 16, width: '80%', marginTop: 8 }} />
              <div className="skeleton-text" style={{ height: 40, marginTop: 12, borderRadius: 8 }} />
            </div>
          ) : up && isConversionUpgrade ? (
            <>
              <div className="calc-row">
                <span className="muted">
                  {t('planChangeCalculator.creditLine', { plan: currentPlan.name, days: Math.round(daysRemaining) })}
                </span>
                <span className="v">{formatMoney(credit, currency)}</span>
              </div>
              <div className="calc-row">
                <span className="muted">
                  {t('planChangeCalculator.convertedLine', {
                    price: formatMoney(targetPlan.price_eur_monthly, currency),
                    plan: targetPlan.name,
                  })}
                </span>
                <span className="v">{upUntil}</span>
              </div>
              <div className="calc-row total">
                <span>{t('planChangeCalculator.dueToday')}</span>
                <span className="v" data-testid="calc-due-today">{formatMoney(0, currency)}</span>
              </div>
              <div className="calc-hl up" data-testid="calc-highlight">
                {t('planChangeCalculator.highlightUp', {
                  mobile: quotaLabel(targetQuota.mobile),
                  iot: quotaLabel(targetQuota.iot),
                  date: upUntil,
                })}
              </div>
            </>
          ) : up ? (
            <>
              <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  {t('planChangeCalculator.termLabel')}
                </span>
                <div className="segment" role="group" aria-label={t('planChangeCalculator.termLabel')}>
                  {DURATION_OPTIONS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={d === term ? 'is-active' : ''}
                      onClick={() => setTerm(d)}
                      data-testid={`term-btn-${d}`}
                    >
                      {t('duration.month', { count: d })}
                    </button>
                  ))}
                </div>
                {discountPct != null && discountPct > 0 && (
                  <span className="pill pill-success">{`−${discountPct} %`}</span>
                )}
              </div>

              <div className="calc-row">
                <span className="muted">{`${targetPlan.name} · ${t('duration.month', { count: term })}`}</span>
                <span className="v">{formatMoney(termTotal, currency)}</span>
              </div>
              <div className="calc-row">
                <span className="muted">
                  {t('planChangeCalculator.creditLine', { plan: currentPlan.name, days: Math.round(daysRemaining) })}
                </span>
                <span className="v c-success">{`−${formatMoney(Math.min(credit, termTotal), currency)}`}</span>
              </div>
              <div className="calc-row total">
                <span>{t('planChangeCalculator.dueToday')}</span>
                <span className="v" data-testid="calc-due-today">{formatMoney(due, currency)}</span>
              </div>
              <div className="calc-hl up" data-testid="calc-highlight">
                {t('planChangeCalculator.highlightUp', {
                  mobile: quotaLabel(targetQuota.mobile),
                  iot: quotaLabel(targetQuota.iot),
                  date: upUntil,
                })}
                {overflow > 0 &&
                  ' ' +
                    t('planChangeCalculator.highlightUpOverflow', { days: Math.round(overflow) })}
              </div>
            </>
          ) : (
            <>
              <div className="calc-row">
                <span className="muted">
                  {t('planChangeCalculator.creditLine', { plan: currentPlan.name, days: Math.round(daysRemaining) })}
                </span>
                <span className="v">{formatMoney(credit, currency)}</span>
              </div>
              <div className="calc-row">
                <span className="muted">
                  {t('planChangeCalculator.convertedLine', {
                    price: formatMoney(targetPlan.price_eur_monthly, currency),
                    plan: targetPlan.name,
                  })}
                </span>
                <span className="v">{t('planChangeCalculator.convertedValue', { months: convMonths, days: convDays })}</span>
              </div>
              <div className="calc-row total">
                <span>{t('planChangeCalculator.dueToday')}</span>
                <span className="v" data-testid="calc-due-today">{formatMoney(0, currency)}</span>
              </div>
              <div className="calc-hl down" data-testid="calc-highlight">
                {t('planChangeCalculator.highlightDown', {
                  credit: formatMoney(credit, currency),
                  plan: targetPlan.name,
                  date: downUntil,
                  delta:
                    deltaDays >= 0
                      ? t('planChangeCalculator.deltaMore', { days: deltaDays })
                      : t('planChangeCalculator.deltaFewer', { days: Math.abs(deltaDays) }),
                })}
              </div>
              {overQuota && (
                <div className="warn-quota" role="alert" data-testid="warn-quota">
                  <span className="ic" aria-hidden="true">{'⚠'}</span>
                  <span>
                    {t('planChangeCalculator.quotaWarning', {
                      mobileUsed,
                      iotUsed,
                      plan: targetPlan.name,
                      mobile: quotaLabel(targetQuota.mobile),
                      iot: quotaLabel(targetQuota.iot),
                    })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="row" style={{ gap: 8, marginTop: 16 }}>
        {up && due > 0 ? (
          <Link
            to="/checkout"
            search={{ plan: targetPlan.id, term }}
            className="btn btn-primary"
            data-testid="calc-cta"
          >
            {t('planChangeCalculator.ctaUpgradePay', { amount: formatMoney(due, currency) })}
          </Link>
        ) : (
          <button
            className="btn btn-primary"
            onClick={handleCtaClick}
            disabled={isMutating}
            data-testid="calc-cta"
          >
            {up
              ? t('planChangeCalculator.ctaUpgradeFree')
              : t('planChangeCalculator.ctaDowngrade', { plan: targetPlan.name })}
          </button>
        )}
        <span className="muted" style={{ fontSize: 11 }} data-testid="calc-note">
          {up
            ? due > 0
              ? t('planChangeCalculator.noteUpgradePay')
              : t('planChangeCalculator.noteUpgradeFree')
            : t('planChangeCalculator.noteDowngrade')}
        </span>
      </div>

      {showConfirm && (
        <ConfirmChangeModal
          direction={up ? 'up' : 'down'}
          targetPlanId={targetPlan.id}
          durationMonths={up ? term : 1}
          bodyText={confirmBody}
          changePlan={changePlan}
          onClose={() => setShowConfirm(false)}
          onSuccess={handleConfirmSuccess}
          onError={onError}
        />
      )}
    </div>
  )
}
